import moment from 'moment';
import * as backend from '@/api/backend';
import { checkResponseStatus } from '@/util/check';
import { formatEventForApi } from '@/util/coerce';

const defaultEvent = {
  eventID: null,
  date: moment().format('YYYY-MM-DD'),
  title: '',
  latitude: '49.14948',
  longitude: '9.21611',
  address: 'Weipertstraße 8–10, 74076 Heilbronn',
  addressPreviewName: 'Innovationsfabrik',
  startTime: '03:00:00',
  endTime: '20:00:00',
  price: 1200,
  description: '',
  shortDescription: '',
  maleCount: null,
  femaleCount: null,
  diversCount: null,
  minAge: null,
  maxAge: null,
  participantCount: 5,
  deleted: false,
  eventTagID: 1,
  intervalTypeID: 1,
  interests: [],
  tickets: [],
  eventImageUrl: '',
  eventSlots: [],
  moderatorIDs: [],
};

export default {
  state: {
    events: [],
    loading: false,
    error: false,
    eventEdit: {
      ...defaultEvent,
    },
    unsavedEvents: {},
    defaultEvent,
  },
  mutations: {
    fetch(state) {
      state.loading = true;
    },
    errorEvents(state) {
      state.events = [];
      state.loading = false;
      state.error = true;
    },
    setEvents(state, events) {
      state.events = events;
      state.loading = false;
      state.error = false;
    },
    editEvent(state, event) {
      if (!event) {
        state.eventEdit = JSON.parse(JSON.stringify(state.defaultEvent));
        return;
      }
      const formattedEvent = {
        ...event,
        deleted: !!event.deleted,
      };
      const next = {
        ...JSON.parse(JSON.stringify(state.defaultEvent)),
        ...formattedEvent,
      };
      if (next.startTime) {
        next.startTime = String(next.startTime).slice(0, 5);
      }
      if (next.endTime) {
        next.endTime = String(next.endTime).slice(0, 5);
      }
      state.eventEdit = next;
    },
    updateEvent(state, event) {
      Object.keys(event).forEach((key) => {
        state.eventEdit[key] = event[key];
      });
    },
    addUnsavedEvent(state, event) {
      state.unsavedEvents = {
        ...state.unsavedEvents,
        [event.eventID]: { ...event },
      };
    },
    removeUnsavedEvent(state, event) {
      state.unsavedEvents = {
        ...state.unsavedEvents,
        [event.eventID]: undefined,
      };
    },
  },
  actions: {
    async fetchEvents({ commit }) {
      commit('fetch');
      try {
        const response = await backend.getEvents();
        await checkResponseStatus(200, response);
        const json = response.data;
        const formattedJson = json.map((event) => ({
          ...event,
          eventSlots: event.eventSlots.map((eventSlot) => ({
            ...eventSlot,
            rule: JSON.parse(eventSlot.rule),
          })),
        }));
        commit('setEvents', formattedJson);
      } catch (e) {
        console.error(e);
        commit('errorEvents');
      }
    },
    async createEvent(context, { event }) {
      const formattedEvent = formatEventForApi({
        ...event,
        date: event.date.slice(0, 10),
      });

      const response = await backend.postEvent(formattedEvent);
      await checkResponseStatus(201, response);
      const { eventID } = response.data;
      return { eventID };
    },
    async saveEvent({ commit }, event) {
      try {
        const formattedEvent = formatEventForApi({
          ...event,
          date: event.date.slice(0, 10),
        });
        const response = await backend.patchEvent(formattedEvent.eventID, formattedEvent);
        await checkResponseStatus(200, response);
        commit('removeUnsavedEvent', event);
        return response;
      } catch (error) {
        commit('addUnsavedEvent', event);
        throw error;
      }
    },
    async deleteEvent(context, { eventID }) {
      const response = await backend.deleteEvent(eventID);
      await checkResponseStatus(200, response);
    },
    async saveEventImage(context, { eventID, file }) {
      const response = await backend.postImage(`/image/event/${eventID}`, file);
      await checkResponseStatus([200, 201], response);
      return response;
    },
    async deleteEventImage(context, { eventID }) {
      const response = await backend.deleteImage(`/image/event/${eventID}`);
      await checkResponseStatus([200, 204], response);
      return response;
    },
    async postTicket(context, ticket) {
      const response = await backend.postTicket(ticket);
      await checkResponseStatus(201, response);
      const data = response.data;
      return data;
    },
    async deleteTicket(context, { ticketID }) {
      const response = await backend.deleteTicket(ticketID);
      await checkResponseStatus(200, response);

      const data = response.data;
      if (!data || data.statusCode !== 'success') {
        throw new Error('Unexpected server response');
      }
    },
  },
  getters: {
  },
  namespaced: true,
};
